import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SlideOver } from '@/components/SlideOver.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import {
  editPayment,
  type EditPaymentInput,
  type PaymentRecord,
} from '@/lib/api/payments.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import {
  CARD_TYPE_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  paymentCardType,
  paymentLastFour,
  paymentMethodLabel,
  paymentTransactionId,
  staffShortName,
} from '@/lib/payment-display.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '@/lib/datetime-local.js';
import { useAuthStore } from '@/stores/auth.js';
import { roleMeetsMinimum } from '@/lib/permissions.js';

export function EditPaymentSlideOver({
  open,
  payment,
  ticketTotal,
  onClose,
  onSuccess,
}: {
  open: boolean;
  payment: PaymentRecord | null;
  ticketTotal?: string | number | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { requireOverride, modalProps } = useManagerOverride();
  const userRole = useAuthStore((s) => s.user?.role ?? 'read_only');
  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState('');
  const [paidAtLocal, setPaidAtLocal] = useState('');
  const [acknowledgeClosedSession, setAcknowledgeClosedSession] = useState(false);
  const [closedSessionWarning, setClosedSessionWarning] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState('');
  const [cardType, setCardType] = useState('visa');
  const [lastFour, setLastFour] = useState('');
  const [notes, setNotes] = useState('');
  const [editNotes, setEditNotes] = useState('');

  useEffect(() => {
    if (!open || !payment) return;
    setMethod(payment.method || 'cash');
    setAmount(String(Number(payment.amount)));
    setPaidAtLocal(toDateTimeLocalValue(payment.paidAt ?? payment.createdAt));
    setAcknowledgeClosedSession(false);
    setClosedSessionWarning(null);
    setTransactionId(paymentTransactionId(payment) ?? '');
    setCardType(paymentCardType(payment) ?? 'visa');
    setLastFour(paymentLastFour(payment) ?? '');
    setNotes(payment.notes ?? '');
    setEditNotes('');
  }, [open, payment]);

  const mutation = useMutation({
    mutationFn: (body: EditPaymentInput) => editPayment(payment!.id, body),
    onSuccess: () => {
      toastSuccess('Payment updated');
      onSuccess();
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      if (parsed.code === 'CLOSED_SESSION_WARNING') {
        setClosedSessionWarning(parsed.message);
        return;
      }
      toastError(parsed.message, parsed.code);
    },
  });

  if (!payment) return null;

  const originalAmount = Number(payment.amount);
  const amountValue = Number(amount);
  const amountChanged = Number.isFinite(amountValue) && Math.abs(amountValue - originalAmount) > 0.001;
  const methodChanged = method !== payment.method;
  const originalPaidAt = payment.paidAt ?? payment.createdAt;
  let paidAtPreview: Date | null = null;
  try {
    paidAtPreview = paidAtLocal ? new Date(fromDateTimeLocalValue(paidAtLocal)) : null;
  } catch {
    paidAtPreview = null;
  }
  const paidAtChanged =
    paidAtPreview != null &&
    Math.abs(paidAtPreview.getTime() - new Date(originalPaidAt).getTime()) > 1000;
  const backdating =
    paidAtChanged && paidAtPreview != null && paidAtPreview.getTime() < Date.now() - 60_000;
  const needsOverride = amountChanged || methodChanged || backdating;
  const canBackdate = roleMeetsMinimum(userRole, 'store_manager');
  const recordedBy = payment.recordedBy ? staffShortName(payment.recordedBy) : '';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toastError('Amount must be greater than zero');
      return;
    }
    if (ticketTotal != null && amountValue > Number(ticketTotal) + 0.001) {
      toastError('Amount cannot exceed ticket total');
      return;
    }
    if (!editNotes.trim()) {
      toastError('Reason for edit is required');
      return;
    }
    if (!paidAtLocal) {
      toastError('Payment date is required');
      return;
    }
    let paidAtIso: string;
    try {
      paidAtIso = fromDateTimeLocalValue(paidAtLocal);
    } catch {
      toastError('Invalid payment date');
      return;
    }
    if (new Date(paidAtIso).getTime() > Date.now() + 60_000) {
      toastError('Payment date cannot be in the future');
      return;
    }
    if (backdating && !canBackdate) {
      toastError('Backdating a payment requires store manager or higher');
      return;
    }
    if (closedSessionWarning && !acknowledgeClosedSession) {
      toastError('Acknowledge the closed-session warning to continue');
      return;
    }

    let managerOverrideAuthorizedBy: string | undefined;
    if (needsOverride) {
      const result = await requireOverride({
        action: 'payment.edit',
        title: backdating ? 'Manager Approval to Backdate Payment' : 'Manager Approval to Edit Payment',
        description: backdating
          ? `Backdating payment to ${formatDateTime(paidAtIso)}`
          : `Editing ${paymentMethodLabel(payment.method)} payment of ${formatCurrency(payment.amount)}`,
        context: {
          amount: amountValue,
          reason: editNotes.trim(),
        },
      });
      if (!result.approved) return;
      managerOverrideAuthorizedBy = result.authorizedBy.id;
    }

    const body: EditPaymentInput = {
      amount: amountValue,
      method,
      paidAt: paidAtIso,
      notes: notes.trim() || null,
      editNotes: editNotes.trim(),
      ...(acknowledgeClosedSession ? { acknowledgeClosedSession: true } : {}),
      ...(managerOverrideAuthorizedBy ? { managerOverrideAuthorizedBy } : {}),
    };

    if (method === 'card') {
      body.transactionId = transactionId.trim() || null;
      body.cardType = cardType || null;
      body.lastFour = lastFour.trim() || null;
    } else {
      body.transactionId = transactionId.trim() || null;
    }

    mutation.mutate(body);
  }

  return (
    <>
      <SlideOver title="Edit Payment" open={open} onClose={onClose} panelClassName="max-w-lg">
        <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <p className="font-semibold text-slate-950">
              Original: {paymentMethodLabel(payment.method)} · {formatCurrency(payment.amount)}
            </p>
            <p className="mt-1 text-slate-500">
              Recorded: {formatDateTime(payment.paidAt ?? payment.createdAt)}
              {recordedBy ? ` by ${recordedBy}` : ''}
            </p>
          </div>

          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Payment date/time *</span>
            <input
              type="datetime-local"
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={paidAtLocal}
              onChange={(e) => {
                setPaidAtLocal(e.target.value);
                setClosedSessionWarning(null);
                setAcknowledgeClosedSession(false);
              }}
              required
            />
            {backdating ? (
              <p className="text-xs text-amber-800">
                Backdating requires store manager+ and manager PIN override.
              </p>
            ) : null}
          </label>

          {closedSessionWarning ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-950">
              <p className="font-semibold">{closedSessionWarning}</p>
              <label className="mt-2 flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={acknowledgeClosedSession}
                  onChange={(e) => setAcknowledgeClosedSession(e.target.checked)}
                />
                <span>I understand this will make the Z-report for that day no longer match.</span>
              </label>
            </div>
          ) : null}

          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Payment Method</span>
            <select
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              {PAYMENT_METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Amount *</span>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                $
              </span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                className="w-full rounded-xl border border-slate-300 py-2.5 pl-7 pr-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Transaction ID</span>
            <input
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              placeholder={method === 'card' ? 'For card payments' : 'Optional'}
            />
          </label>

          {method === 'card' ? (
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1.5">
                <span className="text-sm font-semibold text-slate-700">Card Type</span>
                <select
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  value={cardType}
                  onChange={(e) => setCardType(e.target.value)}
                >
                  {CARD_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-semibold text-slate-700">Last 4</span>
                <input
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  value={lastFour}
                  maxLength={4}
                  onChange={(e) => setLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="1234"
                />
              </label>
            </div>
          ) : null}

          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Notes</span>
            <textarea
              className="min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Reason for edit *</span>
            <input
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder='e.g. "Entered wrong amount"'
              required
            />
          </label>

          {needsOverride ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {backdating
                ? 'Backdating requires manager approval.'
                : 'Changing amount or method requires manager approval.'}
            </p>
          ) : null}

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </SlideOver>
      {modalProps ? <ManagerOverrideModal {...modalProps} /> : null}
    </>
  );
}
